import { Suspense, createElement } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Loader from "@UI/Loader/Loader";
import { useLazyRouteMap } from "@hooks/useLazyRouteMap";
import {
  getDefaultRouteForRoles,
  AUTH_REDIRECT_PATHS,
  AUTH_STANDALONE_ROUTES,
  AUTH_ROUTE_GROUPS,
  AUTH_LAZY_ROUTES,
} from "@constants/routeConfig";
import { ROUTE_WRAPPERS } from "@constants/routeWrappers";
import RequireAuth from "@utils/RequireAuth";
import NotFound from "@components/Routes/NotFound";
import { getAuthReturnPath } from "../../utils/authReturnPath";
import { getPaymentReturnPath } from "../../utils/paymentReturnPath";

/** Redirects to default home by role when already authenticated */
const RedirectByRole = () => {
  const roles = useSelector((state) => state.user?.roles) || [];
  const location = useLocation();
  const target = new URLSearchParams(location.search).get("returnTo");
  const returnTo =
    getPaymentReturnPath(target) ||
    getAuthReturnPath(target || location.state?.from, roles);
  return <Navigate to={returnTo || getDefaultRouteForRoles(roles)} replace />;
};

const AuthenticatedRoutes = () => {
  const lazyByPath = useLazyRouteMap(AUTH_LAZY_ROUTES);

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        {AUTH_REDIRECT_PATHS.map((path) => (
          <Route key={path} path={path} element={<RedirectByRole />} />
        ))}
        {AUTH_STANDALONE_ROUTES.map((r) => {
          const Page = lazyByPath[r.path];
          return <Route key={r.path} path={r.path} element={<Page />} />;
        })}
        {AUTH_ROUTE_GROUPS.map((group) => (
          <Route
            key={group.allowedRoles.join(",")}
            element={<RequireAuth allowedRoles={group.allowedRoles} />}
          >
            {group.routes.map((r) => {
              if (r.redirect) {
                return (
                  <Route
                    key={r.path}
                    path={r.path}
                    element={<Navigate to={r.redirect} replace />}
                  />
                );
              }
              if (r.children) {
                const LayoutEl = lazyByPath[r.path];
                const layoutElement = LayoutEl ? createElement(LayoutEl) : null;
                return (
                  <Route key={r.path} path={r.path} element={layoutElement}>
                    {r.children.map((child) => {
                      if (child.redirect) {
                        return (
                          <Route
                            key={child.path}
                            path={child.path}
                            element={<Navigate to={child.redirect} replace />}
                          />
                        );
                      }
                      const ChildEl = lazyByPath[`${r.path}/${child.path}`];
                      return (
                        <Route
                          key={child.path}
                          path={child.path}
                          element={ChildEl ? createElement(ChildEl) : null}
                        />
                      );
                    })}
                  </Route>
                );
              }
              const LazyEl = lazyByPath[r.path];
              const element = LazyEl ? <LazyEl /> : null;
              const Wrapper = r.wrapper
                ? (ROUTE_WRAPPERS[r.wrapper] ?? null)
                : null;
              if (r.wrapper && !Wrapper) {
                console.warn(
                  `Route wrapper "${r.wrapper}" not found in ROUTE_WRAPPERS (route: ${r.path})`,
                );
              }
              const wrapped = Wrapper
                ? createElement(Wrapper, null, element)
                : element;
              return <Route key={r.path} path={r.path} element={wrapped} />;
            })}
          </Route>
        ))}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AuthenticatedRoutes;
